import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CloudPod } from '@couch/shared';
import { errorMessage } from '../../api/client';
import { CLOUD_PODS_KEY, cloudApi } from '../../api/cloud';
import { ConsoleLink } from './CloudBadge';
import { perHour } from './NewCloudMachineDialog';

function WaitingState({ pod }: { pod: CloudPod }) {
  if (pod.state === 'creating') return <>Google is looking for an L4 in a European zone…</>;
  if (pod.state === 'failed') return <span className="form-error">{pod.error}</span>;
  if (pod.state === 'installing') return <>Couch is installing itself…</>;
  return pod.provider === 'gcp'
    ? <>did not connect within an hour; see <code>sudo journalctl -u google-startup-scripts</code> on the VM</>
    : <>did not connect within an hour; see <code>/workspace/couch-start.log</code> on the pod</>;
}

function WaitingPod({ pod }: { pod: CloudPod }) {
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () => cloudApi.deletePod(pod.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CLOUD_PODS_KEY }),
  });
  const vm = pod.provider === 'gcp';
  const confirmDelete = () => {
    if (pod.state === 'failed' && pod.dataCenterIds.length === 0) { remove.mutate(); return; } // nothing exists at Google
    const text = vm ? `Delete ${pod.name} at Google Cloud? The VM and its disk are deleted; Google stops billing it.` : `Delete ${pod.name} at RunPod? The pod and its volume are deleted; RunPod stops billing it.`;
    if (window.confirm(text)) remove.mutate();
  };
  return (
    <li>
      <span>
        <strong>{pod.name}</strong> — {pod.gpuName}, {perHour(pod.pricePerHour)} — <WaitingState pod={pod} />
      </span>
      <span className="pod-actions">
        <ConsoleLink href={pod.consoleUrl} provider={pod.provider} />
        {pod.state !== 'creating' && (
          <button type="button" className="button-quiet" aria-label={`Delete ${pod.name}`} disabled={remove.isPending} onClick={confirmDelete}>
            {pod.state === 'failed' && pod.dataCenterIds.length === 0 ? 'Dismiss' : vm ? 'Delete VM' : 'Delete pod'}
          </button>
        )}
      </span>
      {remove.isError && <p role="alert" className="form-error">{errorMessage(remove.error)}</p>}
    </li>
  );
}

/** Pods and VMs Couch created that are not a machine yet (docs 58, 70) — so closing the dialog loses nothing; one that never connects can be deleted. */
export function CloudPodsInstalling() {
  const pods = useQuery({ queryKey: CLOUD_PODS_KEY, queryFn: cloudApi.cloudPods, refetchInterval: 10_000 });
  const waiting = (pods.data?.pods ?? []).filter((p) => p.state !== 'paired');
  if (waiting.length === 0) return null;
  return (
    <section className="pods-installing" aria-labelledby="pods-installing-title">
      <h2 id="pods-installing-title">Cloud machines on their way</h2>
      <ul>{waiting.map((p) => <WaitingPod key={p.id} pod={p} />)}</ul>
    </section>
  );
}
